/**
 * ResilientAutoLogout - Custom AutoLogout wrapper with error handling.
 *
 * This component wraps Backstage's AutoLogout to handle the case where
 * signOut() fails because the session has already expired (401 response).
 * In this scenario, the standard AutoLogout doesn't redirect to the sign-in
 * page, leaving the user stuck on a broken page.
 *
 * This wrapper monitors for authentication errors and forces a redirect
 * to the sign-in page when the session is invalid.
 *
 * Related:
 * - https://github.com/backstage/backstage/issues/26440 (logout redirect bug)
 * - https://backstage.io/docs/auth/autologout/
 *
 * @packageDocumentation
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { AutoLogout } from '@backstage/core-components';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';

/**
 * ResilientAutoLogout component that handles signOut errors gracefully.
 *
 * When AutoLogout triggers signOut() and it fails (e.g., 401 due to expired
 * session), this component detects the error and forces a redirect to the
 * sign-in page by reloading the application.
 */
export const ResilientAutoLogout: React.FC = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Initialize to 0, will be set properly in useEffect to avoid impure function during render
  const lastCheckRef = useRef<number>(0);

  /**
   * Check if the user session is still valid by calling getCredentials().
   * If it throws or returns invalid credentials, redirect to sign-in.
   */
  const checkSessionValidity = useCallback(async () => {
    try {
      const credentials = await identityApi.getCredentials();
      // If no token, session is invalid
      if (!credentials.token) {
        console.warn(
          '[ResilientAutoLogout] Session invalid - no token, redirecting to sign-in',
        );
        // Force reload to trigger sign-in page
        window.location.reload();
      }
    } catch (error) {
      console.warn(
        '[ResilientAutoLogout] Session check failed, redirecting to sign-in:',
        error,
      );
      // Force reload to trigger sign-in page
      window.location.reload();
    }
  }, [identityApi]);

  /**
   * Listen for fetch errors that indicate authentication failures.
   * This catches the case where signOut() fails with 401.
   */
  useEffect(() => {
    const handleFetchError = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      // Check for auth-related fetch failures
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('Unauthorized') ||
          error.message.includes('Not authenticated'))
      ) {
        console.warn(
          '[ResilientAutoLogout] Auth error detected, checking session validity',
        );
        // Small delay to avoid race conditions with other error handlers
        setTimeout(() => checkSessionValidity(), 500);
      }
    };

    window.addEventListener('unhandledrejection', handleFetchError);

    return () => {
      window.removeEventListener('unhandledrejection', handleFetchError);
    };
  }, [checkSessionValidity]);

  /**
   * Periodic session check as a fallback mechanism.
   * Runs every 5 minutes to ensure stale sessions are detected.
   */
  useEffect(() => {
    // Only enable periodic check if autologout is enabled
    const autologoutEnabled = configApi.getOptionalBoolean(
      'auth.autologout.enabled',
    );
    if (autologoutEnabled === false) {
      return;
    }

    // Check session every 5 minutes
    const CHECK_INTERVAL_MS = 5 * 60 * 1000;

    // Initialize lastCheckRef on mount (moved from render to avoid impure function call)
    lastCheckRef.current = Date.now();

    checkIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastCheckRef.current;

      // If the tab was in background and timer was delayed, check session
      if (timeSinceLastCheck > CHECK_INTERVAL_MS * 1.5) {
        console.info(
          '[ResilientAutoLogout] Tab returned from background, checking session',
        );
        checkSessionValidity();
      }

      lastCheckRef.current = now;
    }, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [configApi, checkSessionValidity]);

  // Render the standard AutoLogout component
  // All configuration is read from app-config.yaml auth.autologout
  return <AutoLogout />;
};

export default ResilientAutoLogout;
